<script setup lang="ts">
import {
  UserInterfaceDynamicFormBuilderInput,
  UserInterfaceDynamicFormBuilderDropDownTextInput,
  UserInterfaceDynamicFormBuilderCheckbox,
  LazyUserInterfaceDynamicFormBuilderSelect,
  UserInterfaceDynamicFormBuilderToggle,
  UserInterfaceDynamicFormBuilderDatePicker,
  UserInterfaceDynamicFormBuilderRadioGroup,
  UserInterfaceDynamicFormBuilderPassword,
  UserInterfaceDynamicFormBuilderTextArea,
  UserInterfaceDynamicFormBuilderLabel,
} from '#components';
import { generateTranslationKey } from '~/utilities/data-transformers';
import type {
  IBuilderProps,
  IElement,
  IFormBuilderValidator,
  ITranspiledSchema,
  TModelValue,
} from '~/interfaces/form/formBuilder';
import useFormListeners from '~/composables/forms/useFormListeners';
import { z } from 'zod';
import { trackInitInteraction } from '~/utilities/form-builder.reg';
import type { ModelRef } from 'vue';

interface IForm {
  model: ModelRef<TModelValue>;
  derivedModel: ComputedRef<{ [key: string]: TModelValue }>;
  loaded: Ref<boolean>;
  validated: Ref<boolean>;
  invalid: Ref<boolean>;
  formErrors: Ref<{
    [p: string]: string;
  }>;
  schema: ComputedRef<ITranspiledSchema>;
  validator: Ref<IFormBuilderValidator>;
  isEmpty: Ref<boolean>;
  hasRecaptcha: Ref<boolean>;
  steps: {
    activeStep: Ref<number>;
    isAtFirstStep: ComputedRef<boolean>;
    isAtLastStep: ComputedRef<boolean>;
    availableSteps: ComputedRef<
      {
        validated: boolean;
        invalid: boolean;
        stepModel: {
          [p: string]: string | number | boolean | Date | string[];
        };
      }[]
    >;
    next: () => void;
    previous: () => void;
  };
}

const props = withDefaults(defineProps<IBuilderProps>(), {
  shouldAutoFocus: true,
  showTitle: false,
  showSubTitle: false,
  titleClasses: null,
  formClasses: null,
  inputContainerClasses: null,
  stepContainerClasses: null,
  elementContainerClasses: null,
  subTitleClasses: null,
  containerClasses: null,
});
const { data, allNumbers, disableAddAndUpdateButton } =
  useMobileNumberHandler();
const { $trackEvent, $trackRegIntent } = useNuxtApp();
const emit = defineEmits<{
  (e: 'loaded'): void;
  (e: 'loaded'): void;
  (e: 'edited'): void;
  (e: 'emptySchema', emptySchema: string): void;
  (e: 'error', error: string): void;
}>();

const containerClassList = computed(() => [
  props.containerClasses,
  'transition-all duration-200',
]);

const getElementComponent = (element: IElement) => {
  const componentMap = {
    label: UserInterfaceDynamicFormBuilderLabel,
    text: UserInterfaceDynamicFormBuilderInput,
    textarea: UserInterfaceDynamicFormBuilderTextArea,
    tel: UserInterfaceDynamicFormBuilderInput,
    number: UserInterfaceDynamicFormBuilderInput,
    email: UserInterfaceDynamicFormBuilderInput,
    password: UserInterfaceDynamicFormBuilderPassword,
    dropDownTextInput: UserInterfaceDynamicFormBuilderDropDownTextInput,
    checkbox: UserInterfaceDynamicFormBuilderCheckbox,
    dropdown: LazyUserInterfaceDynamicFormBuilderSelect,
    toggle: UserInterfaceDynamicFormBuilderToggle,
    calendar: UserInterfaceDynamicFormBuilderDatePicker,
    'radio group': UserInterfaceDynamicFormBuilderRadioGroup,
  };
  return (
    componentMap[element.type] ||
    componentMap[element.inputType] ||
    'UserInterfaceDynamicFormInput'
  );
};

const getElementProps = (element: IElement) => ({
  element,
  ...(element.type !== 'toggle' && { formErrors: formErrors.value }),
  ...(element.inputType === 'password' && {
    'form-title': formSchema.value?.formTitle,
  }),
  ...(element.props || {}),
});

const model = defineModel<TModelValue>();
const formSchema = ref(null);
const schemaValidator = ref({});

const edited = ref<boolean>(false);
const loaded = ref<boolean>(false);
const hasRecaptcha = ref<boolean>(false);
const isEmpty = ref<boolean>(true);
const validator = ref<IFormBuilderValidator>();
const validated = ref<boolean>(false);
const formErrors = ref<{ [key: string]: string }>({});
const activeStep = ref<number>(0);
const formTitle = ref<string>('');

const getSchemaElement = (id: string) =>
  schema.value?.sectionMapping
    ?.flatMap((section) => section.elementMapping.elements)
    .find((element) => element.id === id);

const isEmptyValue = (value: TModelValue) =>
  value === '' || value === null || value === undefined;

const shouldSkipValidation = (id: string) => {
  const element = getSchemaElement(id);
  return !!element && !element.required && isEmptyValue(model.value?.[id]);
};

const invalid = computed(() => {
  if (formTitle.value === 'add number' && disableAddAndUpdateButton.value) {
    if (
      allNumbers.value?.some((number) => number.mobileNumber === data.newNumber)
    )
      formErrors.value['newNumber'] = 'validation.new-number.already-exists';
    return true;
  }
  if (!formErrors.value || !validator.value) return true;
  return (
    Object.keys(formErrors.value).length > 0 ||
    Object.keys(validator.value).some((key) => {
      if (shouldSkipValidation(key)) return false;
      if (schema.value?.hasSteps)
        return availableSteps.value.filter((step) => step.invalid).length > 0;
      return key === 'dateOfBirth' && typeof model.value[key] !== 'string'
        ? !validator?.value?.[key]?.safeParse(model.value?.[key]?.toISOString())
            .success
        : !validator?.value?.[key]?.safeParse(model.value[key]).success;
    })
  );
});
const schema = computed(() => formSchema?.value?.schema);
const sectionMapping = computed(() => schema?.value?.sectionMapping);
const availableSteps = computed(() => {
  if (!sectionMapping.value) return [];
  return sectionMapping.value.map((section) => {
    const elementResults = new Map();
    section.elementMapping.elements.forEach((element) => {
      const value = model.value?.[element.id];
      const hasValue = Boolean(value);
      const validator = schemaValidator.value[element.id];
      const isValid =
        !element.required && isEmptyValue(value)
          ? true
          : element.id === 'dateOfBirth' && typeof value !== 'string'
            ? validator.safeParse((value as Date)?.toISOString()).success
            : validator.safeParse(value).success;

      elementResults.set(element.id, { hasValue, isValid });
    });
    const results = Array.from(elementResults.values());
    return {
      validated: results.some((result) => result.hasValue),
      invalid: results.some((result) => !result.isValid),
      stepModel: Object.fromEntries(
        section.elementMapping.elements.map((element) => [
          element.id,
          model.value?.[element.id],
        ]),
      ),
    };
  });
});
const isAtFirstStep = computed(() => activeStep.value === 0);
const isAtLastStep = computed(
  () => activeStep.value === availableSteps.value.length - 1,
);

const loadFormData = async () => {
  const { formSchema: loadedSchema, schemaValidator: loadedValidator } =
    await useFormBuilder(props.templateName);
  formSchema.value = loadedSchema.value;
  schemaValidator.value = loadedValidator.value;

  formTitle.value = formSchema.value?.formTitle;
  validator.value = schemaValidator.value;
  isEmpty.value = formSchema.value?.isEmpty;
  hasRecaptcha.value = formSchema.value?.hasRecaptcha;
};

const form = ref<IForm>({
  model,
  derivedModel: computed(() => {
    const model = {};
    formSchema.value?.schema?.sectionMapping?.forEach((section) => {
      section.elementMapping.elements.forEach((element) => {
        switch (element.inputType) {
          case 'text':
          case 'email':
          case 'tel':
          case 'textarea':
          case 'dropDownTextInput':
          case 'password':
          case 'dropdown':
            model[element.id] = '';
            break;
          case 'checkbox':
            model[element.id] = false;
            break;
          case 'calendar':
            model[element.id] = null;
            break;
          case 'number':
            model[element.id] = 0;
            break;
          default:
            break;
        }
      });
    });
    return model;
  }),
  loaded,
  validated,
  invalid,
  formErrors,
  validator,
  isEmpty,
  schema,
  hasRecaptcha,
  steps: {
    activeStep,
    isAtFirstStep,
    isAtLastStep,
    availableSteps,
    next: () => activeStep.value++,
    previous: () => activeStep.value--,
  },
});

defineExpose(form.value);

const assignEventListeners = () => {
  nextTick(() => useFormListeners(props.templateName, form));
};

const handleFalseySchema = () => {
  emit('emptySchema', 'empty-schema');
};

const handleSchemaLoad = () => {
  if (!formSchema.value) {
    handleFalseySchema();
  } else if (formSchema.value.isEmpty) {
    handleFalseySchema();
  }
  if (!!formSchema.value) assignEventListeners();
  emit('loaded');

  setTimeout(() => {
    if (!props.shouldAutoFocus) return;
    const firstElementId = Object.keys(model.value)[0];
    const firstElement = document.getElementById(firstElementId);
    firstElement?.focus();
    firstElement?.click();
  }, 1000);
  loaded.value = true;
};

const submitHandler = (e) => {
  e.preventDefault();
};

const validateAgainstSchemaValidator = (id: string) => {
  if (formSchema.value.formTitle === 'registration') trackInitInteraction();
  try {
    if (id === 'idNumber' && !!formErrors.value[id]) return;
    if (shouldSkipValidation(id)) return;
    if (model.value[id] !== '' && id in schemaValidator.value) {
      if (id === 'dateOfBirth' && typeof model.value[id] !== 'string')
        schemaValidator.value[id].parse(
          (model.value[id] as Date).toISOString(),
        );
      else schemaValidator.value[id].parse(model.value[id]);
    }
  } catch (err) {
    if (err instanceof z.ZodError) {
      formErrors.value = {
        ...formErrors.value,
        [id]: generateTranslationKey(
          err.errors.length > 1 ? err.errors[1].message : err.errors[0].message,
        ),
      };
    }
  }
};

const handlePasswordConfirmationValidation = (id: string) => {
  if (id === 'passwordConfirmation') {
    if (
      model.value['password'] !== model.value['passwordConfirmation'] &&
      model.value['passwordConfirmation'] !== ''
    )
      formErrors.value = {
        ...formErrors.value,
        passwordConfirmation: 'validation.password-confirmation.does-not-match',
      };
  }
  if (id === 'password')
    if (
      !!model.value['passwordConfirmation'] &&
      model.value['password'] === model.value['passwordConfirmation']
    ) {
      if (
        formErrors.value['passwordConfirmation'] ===
        'validation.password-confirmation.does-not-match'
      )
        delete formErrors.value['passwordConfirmation'];
    } else if (
      'passwordConfirmation' in model.value &&
      'passwordConfirmation' in validator.value &&
      model.value?.['passwordConfirmation'] !== ''
    )
      formErrors.value = {
        ...formErrors.value,
        passwordConfirmation: 'validation.password-confirmation.does-not-match',
      };
};

const handleSinglePageFormValidation = (id: string) => {
  validateAgainstSchemaValidator(id);
  handlePasswordConfirmationValidation(id);
};

const handleMultiPageFormValidation = (id: string) => {
  if (id in availableSteps.value[activeStep.value].stepModel)
    validateAgainstSchemaValidator(id);
};

const handleCheckBoxes = (id: string) => {
  setTimeout(() => {
    const el = document.getElementById(id) as HTMLInputElement;
    if (!!el && 'type' in el && el.type === 'checkbox') {
      const agreeToAll = document.getElementById(
        'agreeToAll',
      ) as HTMLInputElement;
      if (!!agreeToAll) {
        const checkboxes = form.value?.schema?.sectionMapping?.map(
          (section) => {
            return section.elementMapping.elements.filter(
              (element) =>
                element.inputType === 'checkbox' &&
                element.id !== 'agreeToAll',
            );
          },
        )[0];
        if (el === agreeToAll) {
          checkboxes.forEach((checkbox) => {
            if (checkbox.id !== 'agreeToAll') {
              model.value[checkbox.id] = model.value['agreeToAll'];
            }
          });
        } else {
          model.value['agreeToAll'] = checkboxes.every(
            (checkbox) => !!model.value[checkbox.id],
          );
        }
      }
      if ('terms' in model.value) {
        if (!model.value['terms']) {
          formErrors.value['terms'] = 'validation.terms.regex';
        } else {
          delete formErrors.value['terms'];
        }
      }

    }
  }, 100);
};
const onChangeHandler = (id: string) => {
  delete formErrors.value[id];
  validated.value = true;
  const element = document.getElementById(id) as HTMLInputElement;
  if (!!element && element.type === 'checkbox') {
    handleCheckBoxes(id);
    return;
  }
  if (schema.value?.hasSteps) {
    handleMultiPageFormValidation(id);
  } else handleSinglePageFormValidation(id);
  if (!edited.value) {
    edited.value = true;
    $trackEvent('FormEventEnum', 'FORM_START', {
      form_name: props?.templateName,
    });
    if (['registration', 'complete registration'].includes(props.templateName))
      $trackRegIntent({
        form_name: props?.templateName,
      });
  }
  emit('edited');
};

watchEffect(() => {
  if (formSchema.value) handleSchemaLoad();
  if (schema.value?.hasSteps)
    validated.value =
      availableSteps.value?.every((step) => step.validated) ?? false;
});
onMounted(async () => await loadFormData());
</script>
<template>
  <div v-if="!isEmpty">
    <div :class="containerClassList" data-element="container">
      <slot name="form-header" :form="form" />
      <div v-if="!$slots.formHeader && (showTitle || showSubTitle)">
        <UserInterfaceDynamicFormBuilderHeader
          :show-title="showTitle"
          :show-sub-title="showSubTitle"
          :title-classes="titleClasses"
          :form-title="formSchema?.formTitle"
          :form-sub-title="formSchema?.formSubTitle"
          :sub-title-classes="subTitleClasses"
        />
      </div>
      <form
        :id="`${templateName}-form`"
        :class="formClasses"
        class="transition-all duration-200 ease-in-out"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        data-element="form"
        @change="onChangeHandler($event.target.id)"
        @input="onChangeHandler($event.target.id)"
        @submit="submitHandler($event)"
      >
        <slot name="before" />
        <div
          v-for="(section, sectionIndex) in sectionMapping"
          v-show="activeStep === sectionIndex"
          :key="`${section.id}-${section?.sectionTitle}`"
          :class="stepContainerClasses"
          :data-element="`step-${sectionIndex}-container`"
        >
          <div
            class="flex flex-col gap-2"
            :class="elementContainerClasses"
            data-element="elementContainer"
          >
            <div
              v-for="(element, inputIndex) in section.elementMapping.elements"
              :key="`${element.id.replace(' ', '')}-${element.name}`"
              :class="inputContainerClasses"
              :data-element="`input-${element.id}-container`"
            >
              <component
                :is="getElementComponent(element)"
                :key="element.id"
                v-model="model[element.id]"
                v-bind="getElementProps(element)"
                @input-change="onChangeHandler($event)"
                @update:model-value="
                  model[element.id] = $event;
                  onChangeHandler(element.id);
                "
              >
                <template #suffix>
                  <slot name="input-suffix" />
                </template>
              </component>
              <UserInterfaceDynamicFormBuilderErrorMessage
                v-if="!!formErrors[element.id]"
                :id="`${element.id}-error`"
                :message="!!formErrors[element.id] && formErrors[element.id]"
              />
              <slot :name="`input-${inputIndex}`" />
            </div>
          </div>
          <slot :name="`section-${sectionIndex}`" />
        </div>
      </form>
    </div>
    <slot name="form-actions" :form="form" />
  </div>
</template>
